// ONE Subscriptions list for EVERY PSP — identical tiles, columns, labels and behaviour regardless of
// provider. The shell owns the stat tiles, the table columns, the view filters and all layout; the per-PSP
// config supplies ONLY data: endpoints, a row key, the PSP dialogs, and a normalize(sub) that maps that
// PSP's raw subscription into the one common shape the shell renders. Switching PSP never changes what this
// screen shows — only the numbers in it.
//
// config = {
//   provider, title, blurb, syncTitle, buildNav(active, account, navigate),
//   accounts: bool,                          // AuthNet (multi-account) shows the account dropdown
//   load(fresh) -> { subscriptions, accounts? },
//   loadMaps() -> [{...}],  mapKeyOfMapping(m) -> key,  mapKey(sub) -> key,
//   loadExtra?() -> any,                     // e.g. AuthNet failure sweep; exposed as ctx.extra
//   rowKey(sub), unmap(sub), searchText(sub, ctx),
//   statusOptions?: [{value,label}] | (ctx)=>[...],  matchesStatus(sub, statusFilter),  // the status dropdown (PSP vocab)
//   normalize(sub, { extra }) -> {           // THE common shape the shared tiles + columns read:
//     name,               // subscription name for the "Subscription" column, or null
//     amount, status,     // amount (number) + raw status string (for the chip)
//     active, expired, expiredNeedsReview,   // booleans
//     paidCount, unbookedCount, failedCount, // numbers (unbookedCount drives the Backfill action)
//     nextPaymentDate,    // string|null
//     installments,       // "7 of 10" style label, or null
//   },
//   detailSlot,                              // slot name the Sub-ID / name click opens (PSP detail dialog)
//   mapApi, renderDialogs?(ctx), headerActions?(ctx), onMapped?(savedSub, ctx),
// }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress,
  Alert, Snackbar, TextField, Pagination, FormControl, Select, MenuItem, IconButton, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../common/PageLayout';
import WorkspaceHeader from './WorkspaceHeader';
import SubscriptionMappingDialog from './SubscriptionMappingDialog';
import SubscriptionDetailDialog from '../pledges/SubscriptionDetailDialog';
import DonorLink from './DonorLink';
import { fmtLastSynced, fmtMoney, fmtDate, pledgeStatusMeta } from './reconShared';

const PER = 25;

function StatTile({ label, value, sub, color, onClick, active }) {
  const key = color || 'primary';
  return (
    <Paper variant="outlined" onClick={onClick}
      sx={(theme) => ({
        p: 2, height: '100%', position: 'relative', borderRadius: 2,
        border: active ? 2 : 1, borderColor: active ? `${key}.main` : 'divider',
        borderTop: 3, borderTopColor: color ? `${color}.main` : 'divider',
        bgcolor: active ? alpha(theme.palette[key]?.main || theme.palette.primary.main, 0.12) : 'background.paper',
        boxShadow: active ? 3 : 0, cursor: onClick ? 'pointer' : 'default', transition: '.12s',
        '&:hover': onClick ? { boxShadow: 3, borderColor: `${key}.main` } : {},
      })}>
      {active && <Chip size="small" color={color || 'primary'} label="Showing" sx={{ position: 'absolute', top: 8, right: 8, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }} />}
      <Typography variant="caption" sx={{ color: color ? `${color}.dark` : 'text.secondary', fontWeight: active ? 700 : 600 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: color ? `${color}.dark` : 'text.primary' }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>
    </Paper>
  );
}

// The ONE column set every PSP renders. Each render reads normalized fields (n) + the raw sub (s) for the
// dialogs/handlers. Columns, order, widths and labels are identical for all providers.
const COLUMNS = [
  { key: 'subid', header: 'Sub ID', width: '8%' },
  { key: 'name', header: 'Subscription', width: '15%' },
  { key: 'donor', header: 'Donor', width: '14%' },
  { key: 'amount', header: 'Amount', width: '8%', align: 'right' },
  { key: 'status', header: 'Status', width: '12%' },
  { key: 'payments', header: 'Payments', width: '12%' },
  { key: 'nextpay', header: 'Next payment', width: '11%' },
  { key: 'mapping', header: 'Mapping', width: '12%' },
  { key: 'action', header: 'Action', width: '8%', align: 'right' },
];

export default function SubscriptionsWorkspace({ config }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const account = config.accounts ? (searchParams.get('acct') || searchParams.get('account') || 'all') : null;
  const setAccount = (a) => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('acct', a); return p; }, { replace: true });

  const [data, setData] = useState(null);
  const [maps, setMaps] = useState({});
  const [extra, setExtra] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [syncedAt, setSyncedAt] = useState(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('all');
  const [statusFilter, setStatusFilter] = useState(config.defaultStatus || 'all');
  const [page, setPage] = useState(1);
  const [mapSub, setMapSub] = useState(null);
  const [detailSub, setDetailSub] = useState(null); // opens the shared SubscriptionDetailDialog (same as the Recurring report)
  const [slots, setSlots] = useState({});

  const loadMaps = useCallback(async () => {
    try { const rows = await config.loadMaps(); const m = {}; (rows || []).forEach((x) => { m[config.mapKeyOfMapping(x)] = x; }); setMaps(m); }
    catch { setMaps({}); }
  }, [config]);
  const load = useCallback(async (fresh = false) => {
    setLoading(true); setErr('');
    try {
      const d = await config.load(fresh); setData(d);
      await loadMaps();
      if (config.loadExtra) { try { setExtra(await config.loadExtra()); } catch { /* keep */ } }
      setSyncedAt(new Date().toISOString());
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load subscriptions'); }
    finally { setLoading(false); }
  }, [config, loadMaps]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [account, data, search, view, statusFilter]);

  // Raw subscriptions with their mapping attached, plus the normalized common shape (n) the shell renders.
  const all = useMemo(() => (Array.isArray(data?.subscriptions) ? data.subscriptions : []).map((s) => {
    const mapping = maps[config.mapKey(s)] || null;
    return { s, mapping, n: config.normalize(s, { extra }) };
  }), [data, maps, extra, config]);

  // Drop a column that is blank for THIS PSP and redistribute its width — e.g. "Subscription" (name) is always empty
  // for PaySimple, "Next payment" is always empty for Authorize.Net. Data-driven, so it adapts per provider with no
  // per-PSP column config. (subid/donor/amount/status/payments/mapping/action always apply.)
  const visibleColumns = useMemo(() => {
    const has = (key) => {
      if (key === 'name') return all.some((r) => r.n.name);
      if (key === 'nextpay') return all.some((r) => r.n.nextPaymentDate);
      return true;
    };
    const cols = COLUMNS.filter((c) => (all.length === 0 ? c.key !== 'name' && c.key !== 'nextpay' : has(c.key)));
    const sum = cols.reduce((a, c) => a + parseFloat(c.width), 0) || 100;
    return cols.map((c) => ({ ...c, width: `${((parseFloat(c.width) / sum) * 100).toFixed(2)}%` }));
  }, [all]);

  const openMap = (row) => setMapSub(row.s);
  // Attach the row's mapping to the sub handed to a slot dialog, so a detail dialog can show the mapping
  // (the mapping lives on the row wrapper, not on the raw subscription).
  const openSlot = (name, row) => setSlots((p) => ({ ...p, [name]: { ...row.s, mapping: row.mapping } }));
  const closeSlot = (name) => setSlots((p) => ({ ...p, [name]: null }));
  const doUnmap = async (row) => { try { await config.unmap(row.s); setToast('Mapping removed'); await loadMaps(); } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to remove mapping'); } };

  const ctx = {
    all: all.map((r) => r.s), maps, extra, account, view, setView, statusFilter, setStatusFilter, loading,
    openMap: (s) => setMapSub(s), openSlot: (name, s) => setSlots((p) => ({ ...p, [name]: s })),
    closeSlot, slots,
    unmap: async (s) => { try { await config.unmap(s); setToast('Mapping removed'); await loadMaps(); } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to remove mapping'); } },
    reload: () => load(true), reloadMaps: loadMaps, setToast, setErr,
  };

  const matchesView = (row) => {
    const { n, mapping } = row;
    if (view === 'active') return n.active;
    if (view === 'mapped') return !!mapping;
    if (view === 'unmapped') return !mapping;
    if (view === 'failed') return (n.failedCount || 0) > 0;
    if (view === 'expired') return n.expired;
    return true;
  };
  const sq = search.trim().toLowerCase();
  const rows = all.filter((row) => (!config.accounts || account === 'all' || row.s.account === account)
    && config.matchesStatus(row.s, statusFilter)
    && matchesView(row)
    && (!sq || config.searchText(row.s, ctx).toLowerCase().includes(sq)));
  const pageCount = Math.max(1, Math.ceil(rows.length / PER));
  const pageSafe = Math.min(page, pageCount);
  const paged = rows.slice((pageSafe - 1) * PER, (pageSafe - 1) * PER + PER);

  // ----- shared tiles (identical set + labels for every PSP) -----
  const mappedCount = all.filter((r) => r.mapping).length;
  const activeCount = all.filter((r) => r.n.active).length;
  const expiredRows = all.filter((r) => r.n.expired);
  const expiredReview = expiredRows.filter((r) => r.n.expiredNeedsReview).length;
  const failedSubs = all.filter((r) => (r.n.failedCount || 0) > 0).length;
  const failedAttempts = all.reduce((a, r) => a + (Number(r.n.failedCount) || 0), 0);
  const monthly = all.filter((r) => r.n.active).reduce((a, r) => a + (Number(r.n.amount) || 0), 0);
  const tileView = (v) => () => { setView(view === v ? 'all' : v); setStatusFilter('all'); };
  const tiles = [
    { label: 'Subscriptions', value: all.length, sub: 'all · click to clear', color: 'primary', onClick: () => { setView('all'); setStatusFilter('all'); }, active: view === 'all' && statusFilter === 'all' },
    { label: 'Active', value: activeCount, sub: 'recurring now', color: 'success', onClick: tileView('active'), active: view === 'active' },
    { label: 'Mapped', value: mappedCount, sub: 'devotee + allocation', color: 'success', onClick: tileView('mapped'), active: view === 'mapped' },
    { label: 'Unmapped', value: all.length - mappedCount, sub: 'need setup', color: 'warning', onClick: tileView('unmapped'), active: view === 'unmapped' },
    { label: 'Failed charges', value: failedSubs, sub: `${failedAttempts} attempt${failedAttempts === 1 ? '' : 's'}`, color: 'error', onClick: tileView('failed'), active: view === 'failed' },
    { label: 'Expired', value: expiredRows.length, sub: expiredReview > 0 ? `${expiredReview} need review` : 'terms complete', color: expiredReview > 0 ? 'warning' : 'default', onClick: tileView('expired'), active: view === 'expired' },
    { label: 'Monthly recurring', value: fmtMoney(monthly), sub: 'sum of active', color: null },
  ];

  // ----- shared cell renderers (identical for every PSP) -----
  const renderCell = (key, row) => {
    const { s, mapping, n } = row;
    switch (key) {
      case 'subid':
        return <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem', color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => setDetailSub(s)}>{s.id}</Typography>;
      case 'name':
        return n.name
          ? <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => setDetailSub(s)}>{n.name}</Typography>
          : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>;
      case 'donor':
        return <DonorLink name={s.donor_name} customerId={mapping?.customer_id} />;
      case 'amount':
        return <>{fmtMoney(n.amount)}<Typography variant="caption" sx={{ display: 'block', fontWeight: 400, color: 'text.secondary' }}>/mo</Typography></>;
      case 'status': {
        const warn = (n.failedCount || 0) > 0 || n.expiredNeedsReview;
        const onWarn = (n.failedCount || 0) > 0 && config.failureSlot ? () => openSlot(config.failureSlot, row) : () => setDetailSub(s);
        const warnTitle = (n.failedCount || 0) > 0 ? `${n.failedCount} failed charge(s) — click for details` : 'Expired before its term completed — click to review';
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Chip size="small" variant="outlined" color={n.statusColor || (n.active ? 'success' : 'default')} label={n.status || '—'} />
            {warn && <Tooltip title={warnTitle}><WarningAmberIcon fontSize="small" color="warning" sx={{ cursor: 'pointer' }} onClick={onWarn} /></Tooltip>}
          </Box>
        );
      }
      case 'payments':
        // Uniform booking breakdown (same definitions as the dialog / every tab): settled total, then
        // linked (order carries the id), need-link (order exists, ±2d/±5%), need-order (no order yet).
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Tooltip title="Settled payments on record"><Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{n.installments != null ? n.installments : (n.paidCount != null ? n.paidCount : '—')}</Typography></Tooltip>
            {(n.linkedCount || 0) > 0 && <Tooltip title={`${n.linkedCount} linked to an order`}><Chip size="small" color="success" variant="outlined" label={`${n.linkedCount} linked`} /></Tooltip>}
            {(n.needsLinkCount || 0) > 0 && <Tooltip title={`${n.needsLinkCount} order(s) exist — click the sub to link`}><Chip size="small" color="info" variant="outlined" label={`${n.needsLinkCount} need link`} /></Tooltip>}
            {(n.unbookedCount || 0) > 0 && <Tooltip title={`${n.unbookedCount} with no order — click the sub to create`}><Chip size="small" color="warning" variant="outlined" label={`${n.unbookedCount} need order`} /></Tooltip>}
            {(n.failedCount || 0) > 0 && <Tooltip title={`${n.failedCount} returned/failed`}><Chip size="small" color="error" variant="outlined" label={n.failedCount} /></Tooltip>}
          </Box>
        );
      case 'nextpay':
        return <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{n.nextPaymentDate ? fmtDate(n.nextPaymentDate) : '—'}</Typography>;
      case 'mapping': {
        if (!mapping) return <Chip size="small" color="warning" variant="filled" label="Unmapped" />;
        const po = mapping.pledge_order;
        const pm = po && pledgeStatusMeta(po.status);
        return (<>
          <Chip size="small" color="success" variant="outlined" label={mapping.customer_name || 'Mapped'} sx={{ maxWidth: '100%' }} />
          {(mapping.splits || []).map((sp, i) => <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{sp.label} · {fmtMoney(sp.amount)}</Typography>)}
          {po && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
              <Tooltip title="Open the pledge order">
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate(`/orders/${po.id}/edit`)}>Pledge #{po.order_number || po.id}</Typography>
              </Tooltip>
              {pm && <Chip size="small" variant="outlined" color={pm.color} label={pm.label} sx={{ height: 18, '& .MuiChip-label': { px: 0.6, fontSize: 10 } }} />}
            </Box>
          )}
        </>);
      }
      case 'action':
        return (
          <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
            <Tooltip title={mapping ? 'Edit mapping' : 'Map to devotee'}>
              <IconButton size="small" color="primary" onClick={() => openMap(row)}>{mapping ? <EditOutlinedIcon fontSize="small" /> : <AddLinkIcon fontSize="small" />}</IconButton>
            </Tooltip>
            {mapping && (n.unbookedCount || 0) > 0 && (
              <Tooltip title={`Create ${n.unbookedCount} missing order(s)`}>
                <IconButton size="small" color="success" onClick={() => openSlot('backfill', row)}><PlaylistAddCheckIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
            {mapping && (
              <Tooltip title="Remove mapping">
                <IconButton size="small" sx={{ color: 'text.secondary' }} onClick={() => doUnmap(row)}><LinkOffIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const pager = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{rows.length} subscriptions · {PER} per page</Typography>
      <Pagination count={pageCount} page={pageSafe} onChange={(e, p) => setPage(p)} size="small" color="primary" siblingCount={0} boundaryCount={1} />
    </Box>
  );

  const statusOptions = config.statusOptions
    ? (typeof config.statusOptions === 'function' ? config.statusOptions(ctx) : config.statusOptions)
    : null;
  const filterBar = (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <TextField size="small" placeholder="Search name…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
      {statusOptions && (
        <FormControl size="small">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 170 }}>
            {statusOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      )}
    </Box>
  );

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <WorkspaceHeader
        provider={config.provider} title={config.title || 'Subscriptions'}
        account={account} accounts={data?.accounts || []} onAccount={config.accounts ? setAccount : undefined} accountAllOption={!!config.accounts}
        onSync={() => load(true)} syncing={loading} syncTitle={config.syncTitle || 'Sync now — reload subscriptions'}
        lastSynced={fmtLastSynced(syncedAt)}
        actions={config.headerActions ? config.headerActions(ctx) : undefined}
        sectionNav={config.buildNav('subscriptions', account, navigate)}
        filterBar={filterBar}
      />
      {config.blurb && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{config.blurb}</Typography>}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: `repeat(${tiles.length}, 1fr)` }, gap: 1.5, mb: 2 }}>
        {tiles.map((t, i) => <StatTile key={t.label || i} {...t} />)}
      </Box>

      <Paper variant="outlined">
        {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          : rows.length === 0 ? <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No subscriptions.</Box>
            : (
              <>
                {pager}
                <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& td, & th': { px: 1, verticalAlign: 'middle' } }}>
                  <TableHead>
                    <TableRow>
                      {visibleColumns.map((c) => <TableCell key={c.key} align={c.align} sx={{ width: c.width }}>{c.header}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((row) => (
                      <TableRow key={config.rowKey(row.s)} hover>
                        {visibleColumns.map((c) => <TableCell key={c.key} align={c.align}>{renderCell(c.key, row)}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {pager}
              </>
            )}
      </Paper>

      <SubscriptionMappingDialog
        open={!!mapSub} sub={mapSub} provider={config.provider} account={mapSub?.account} api={config.mapApi}
        onClose={() => setMapSub(null)}
        onSaved={(savedSub) => { setMapSub(null); setToast('Mapping saved'); loadMaps(); if (config.onMapped) config.onMapped(savedSub, ctx); }} />
      <SubscriptionDetailDialog
        open={!!detailSub} provider={config.provider} subId={detailSub?.id} account={detailSub?.account}
        onClose={() => setDetailSub(null)} onChanged={() => load(true)} />
      {config.renderDialogs && config.renderDialogs(ctx)}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageLayout>
  );
}
